import uniqid from 'uniqid';
import template from './templates';
import ReactiveComponent, { filter, watch } from '@utils/reactive-component';
import keyCodes from '@utils/key-codes';
import isFn from '@utils/is-function';

const DEFAULT_OPTIONS = {
    min: 1,
    max: 100,
    replaceOriginEl: false,
    value: 1,
    useTemplate: true
};

const DEFAULT_LABELS = {
    decrement: 'decrement',
    increment: 'increment',
    inputAriaLabel: 'Quantity Selector'
};

export default class QuantitySelector extends ReactiveComponent {
    #disabled = false;
    #onDisabledFn = null;

    constructor($originEl, options, labels) {
        super();

        options = {
            ...DEFAULT_OPTIONS,
            ...options
        };

        labels = {
            ...DEFAULT_LABELS,
            ...labels
        };
        
        const parsed = $.parseHTML(template);
        this.$el = options.useTemplate ? $(parsed) : $originEl;
        this.$originEl = $originEl;

        this.$decrement = this.$el.find('[data-control="decrement"]');
        this.$increment = this.$el.find('[data-control="increment"]');
        this.$input = this.$el.find('[data-control="input"]');
        this.$quantityWrapper = this.$el;

        this.$decrementLabel = this.$el.find('[data-label="decrement"]');
        this.$incrementLabel = this.$el.find('[data-label="increment"]');

        this.id = uniqid.time('quantitySelector_');

        const min = Number(this.$input.data('min'));
        const max = Number(this.$input.data('max'));

        this.min = !isNaN(min) ? min : options.min;
        this.max = !isNaN(max) ? max : options.max;
        this.labels = labels;
        this.backOrdered = options.backOrdered ? options.backOrdered : false;

        let val = this.$input.val() || options.value;
        const value = !isNaN(Number(val)) && val > this.min ? val : this.min;

        this.$input.val(value);

        this.reactive({
            value
        });
        
        this.init();

        if(this.$originEl && options.useTemplate) {
            this.$originEl.replaceWith(parsed);
        }
    }

    [filter]() {
        return { value: this.filterQuantity };
    }

    [watch]() {
        return { value: this.updateState }
    }

    decrement(val = 1) {
        let newVal = this.value - val;
        if(newVal < this.min) {
            newVal = this.min;
        }
        this.value = newVal;
    }

    enable() {
        this.#disabled = false;
        this.#onDisabledFn = null;
        this.$el.removeClass('-disabled');
    }

    disable(onDisabledFn) {
        this.#disabled = true;
        this.$el.addClass('-disabled');

        if(isFn(onDisabledFn)) {
            this.#onDisabledFn = onDisabledFn;
        }
    }

    onDisabledUpdate(deferredValue) {
        if(this.#onDisabledFn) {
            const deferredUpdateFn = () => (this.value = deferredValue);
            this.#onDisabledFn(deferredUpdateFn);
        }
    }

    filterQuantity(val) {
        if(this.#disabled) {
            this.onDisabledUpdate(val);
            return this.value;
        }
        
        let newVal = Number(val) ?? this.min;

        if(newVal < this.min) {
            newVal = this.min;
        } else if(newVal > this.max) {
            // recursive function to keep splicing the first char of newVal until it's less than the max
            const spliceNum = num => {
                newVal = Number(`${num}`.substring(1));
                if(newVal > this.max) {
                    spliceNum(newVal);
                }
            };

            spliceNum(newVal);
        }

        return newVal;
    }

    increment(val = 1) {
        let newVal = this.value + val;
        if(newVal > this.max) {
            newVal = this.max;
        }
        this.value = newVal;
    }
    
    init() {
        this.setupAccessibility();
        this.setLabels();
        this.setButtonState();
        this.setupListeners();
    }

    highlight(isHighlighted = true) {
        if(isHighlighted) {
            this.$el.addClass('-highlight');
        } else {
            this.$el.removeClass('-highlight');
        }
    }

    onButtonFocus() {
        this.$input.get(0).focus();
    }

    onInput(evt) {
        this.value = evt.currentTarget.value;
    }

    onKeydown(evt) {
        const key = evt.which ? evt.which : evt.keyCode;

        // default action if tab, backspace, a digit, or CTRL is pressed
        if(
            key === keyCodes.TAB ||
            key === keyCodes.BACKSPACE ||
            evt.ctrlKey || 
            (key > 47 && key < 58) ||
            (key > 95 && key < 106)
        ) {
            return true;
        }

        if(key === keyCodes.ARROW_RIGHT || key === keyCodes.ARROW_UP) {
            this.increment();
        }

        if(key === keyCodes.PAGE_UP) {
            this.increment(10);
        }

        if(key === keyCodes.ARROW_LEFT || key === keyCodes.ARROW_DOWN) {
            this.decrement();
        }

        if(key === keyCodes.PAGE_DOWN) {
            this.decrement(10);
        }

        return false;
    }

    resetButtons() {
        this.$decrement.removeAttr('aria-disabled');
        this.$decrement.removeClass('-disabled');
        this.$increment.removeAttr('aria-disabled');
        this.$increment.removeClass('-disabled');
    }

    setButtonState() {
        if(this.value === this.min) {
            this.$decrement.attr('aria-disabled', 'true');
            this.$decrement.addClass('-disabled');
        } else if (this.value === this.max) {
            this.$increment.attr('aria-disabled', 'true');
            this.$increment.addClass('-disabled');
        }
    }

    setupAccessibility() {
        this.$input.attr({
            'id': this.id,
            'aria-label': this.labels.inputAriaLabel,
            'aria-valuemin': this.min,
            'aria-valuemax': this.max,
            'aria-valuenow': this.value
        });

        this.$decrement.attr('aria-controls', this.id);
        this.$increment.attr('aria-controls', this.id);
    }

    setLabels() {
        this.$decrementLabel.html(this.labels.increment);
        this.$incrementLabel.html(this.labels.increment);
    }

    setupListeners() {
        this.$decrement.on('click', () => this.decrement());
        this.$decrement.on('focus', () => this.onButtonFocus());
        this.$increment.on('click', () => this.increment());
        this.$increment.on('focus', () => this.onButtonFocus());
        this.$input.on('input', (evt) => this.onInput(evt));
        this.$input.on('keydown', (evt) =>  this.onKeydown(evt));
        if(this.backOrdered){
            this.$quantityWrapper.addClass('-backordered');
            this.disable();
        }
    }

    updateState() {
        this.$input.val(this.value);
        this.$input.attr('aria-valuenow', this.value);

        this.resetButtons();
        this.setButtonState();
    }

    watchValue(fn) {
        if(isFn(fn)) {
            return this.watch('value', fn);
        }
    }
}

$(() => {
    $('.js-quantity-selector').each(function() {
        const $el = $(this);
        const options = {
            useTemplate: false
        };

        new QuantitySelector($el, options);
    });
});
